import { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Col, Form, Row } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import Select from 'components/general/ViewportSelect';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import { loadConfig } from '../../../../../config';

const levels = ['level1', 'level2', 'level3', 'level4', 'level5'];

const toInputDate = (value) => {
	if (!value) return '';

	if (value.includes('/')) {
		const [day, month, year] = value.split('/');
		if (day && month && year) {
			return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
		}
	}

	const parsed = new Date(value);
	if (Number.isNaN(parsed.getTime())) return '';
	return parsed.toISOString().slice(0, 10);
};

const toDateKey = (value) => {
	if (!value) return '';

	if (typeof value === 'string') {
		if (/^\d{4}-\d{2}-\d{2}/.test(value)) {
			return value.slice(0, 10);
		}

		if (value.includes('/')) {
			const [day, month, year] = value.split('/');
			if (day && month && year) {
				return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
			}
		}
	}

	const parsed = new Date(value);
	if (Number.isNaN(parsed.getTime())) return '';
	return parsed.toISOString().slice(0, 10);
};

const getLocalTodayKey = () => {
	const now = new Date();
	const yyyy = now.getFullYear();
	const mm = String(now.getMonth() + 1).padStart(2, '0');
	const dd = String(now.getDate()).padStart(2, '0');
	return `${yyyy}-${mm}-${dd}`;
};

export default function ProjectSelector({
	presenterId,
	item,
	isPlaying,
	canEdit = true,
	onAttemptCreated,
	onAttemptDeleted,
	resetSignal = 0,
	visible = true,
}) {
	const { user: authUser } = useSelector(x => x.auth);
	const { meetingId } = useParams();
	const url = process.env.REACT_APP_URL;

	const [userPathways, setUserPathways] = useState([]);
	const [allPathways, setAllPathways] = useState([]);
	const [catalogError, setCatalogError] = useState('');
	const [catalogLoaded, setCatalogLoaded] = useState(false);
	const [catalogRetry, setCatalogRetry] = useState(0);
	const [pathwaysLoaded, setPathwaysLoaded] = useState(false);
	const [pathwaysError, setPathwaysError] = useState('');
	const [pathwaysRetry, setPathwaysRetry] = useState(0);
	const [selectedUserPathwayId, setSelectedUserPathwayId] = useState('');
	const [selectedProjectId, setSelectedProjectId] = useState('');
	const [speechTitle, setSpeechTitle] = useState('');
	const [scheduledDate, setScheduledDate] = useState('');
	const [loading, setLoading] = useState(false);
	const [existingAttemptMeta, setExistingAttemptMeta] = useState(null);

	useEffect(() => {
		let cancelled = false;

		if (!meetingId || !url) return undefined;

		const hydrateMeetingDate = async () => {
			try {
				const response = await fetch(`${url}/meeting/${meetingId}`);
				if (!response.ok) return;

				const data = await response.json();
				if (cancelled) return;

				const dateValue = data?.meetingDetails?.meetingDate || item?.meetingDate || '';
				setScheduledDate(prev => prev || toInputDate(dateValue));
			} catch (err) {
				// no-op: date field stays editable for manual input
			}
		};

		hydrateMeetingDate();

		return () => {
			cancelled = true;
		};
	}, [meetingId, url, item?.meetingDate]);

	useEffect(() => {
		if (!visible) return;
		setSelectedUserPathwayId('');
		setSelectedProjectId('');
		setSpeechTitle('');
		setExistingAttemptMeta(null);
		setScheduledDate(toInputDate(item?.meetingDate) || '');
	}, [resetSignal, visible, item?.meetingDate]);

	useEffect(() => {
		let cancelled = false;
		setPathwaysError('');
		setPathwaysLoaded(false);
		setUserPathways([]);
		if (!presenterId || !authUser?.token) {
			setUserPathways([]);
			setPathwaysLoaded(false);
			setSelectedUserPathwayId('');
			setSelectedProjectId('');
			setSpeechTitle('');
			setExistingAttemptMeta(null);
			return;
		}

		const getUserPathways = async () => {
			try {
				const response = await fetch(`${url}/userpathway/${presenterId}`, {
					headers: {
						Authorization: `Bearer ${authUser.token}`,
					},
				});

				const data = await response.json();
				if (!response.ok) {
					throw new Error(data?.error || data?.message || `Unable to load assigned pathways (${response.status}).`);
				}
				if (!Array.isArray(data)) throw new Error('The assigned pathways response is invalid.');
				if (cancelled) return;
				setUserPathways(data);
				setPathwaysLoaded(true);
				setSelectedUserPathwayId('');
				setSelectedProjectId('');
				setSpeechTitle('');
				setExistingAttemptMeta(null);
			} catch (err) {
				if (cancelled) return;
				setUserPathways([]);
				setPathwaysError(err.message || 'Unable to load assigned pathways.');
				setPathwaysLoaded(false);
				setSelectedUserPathwayId('');
				setSelectedProjectId('');
				setSpeechTitle('');
				setExistingAttemptMeta(null);
			}
		};

		getUserPathways();
		return () => { cancelled = true; };
	}, [presenterId, authUser?.token, url, pathwaysRetry]);

	useEffect(() => {
		let cancelled = false;
		setCatalogError('');
		setCatalogLoaded(false);
		setAllPathways([]);
		const getPathways = async () => {
			try {
				const config = await loadConfig();
				const pathwaysUrl = String(config?.PATHWAYS_URL || '').trim().replace(/\/+$/, '');
				if (!/^https?:\/\//i.test(pathwaysUrl)) {
					throw new Error('The TM Paths catalogue URL is missing or invalid. Restart Agenda after reloading Dev Control.');
				}
				const response = await fetch(`${pathwaysUrl}/pathways`);
				if (!response.ok) throw new Error(`TM Paths catalogue request failed (${response.status}).`);
				const data = await response.json();
				if (!Array.isArray(data)) throw new Error('The TM Paths catalogue response is invalid.');
				if (cancelled) return;
				setAllPathways(data);
				setCatalogLoaded(true);
			} catch (err) {
				if (!cancelled) setCatalogError(err.message || 'Unable to load the TM Paths catalogue.');
			}
		};

		getPathways();
		return () => { cancelled = true; };
	}, [catalogRetry]);

	const selectedUserPathway = useMemo(
		() => userPathways.find(p => p._id === selectedUserPathwayId) || null,
		[userPathways, selectedUserPathwayId]
	);

	const selectedPathway = useMemo(
		() => allPathways.find(p => p._id === selectedUserPathway?.pathwayId) || null,
		[allPathways, selectedUserPathway?.pathwayId]
	);

	const projectOptions = useMemo(() => {
		if (!selectedPathway) return [];

		const options = [];
		let runningNumber = 1;

		levels.forEach(levelKey => {
			const level = selectedPathway[levelKey] || {};
			const mandatory = (level.mandatoryProjects || []).map(project => ({
				value: project._id,
				label: project.title,
				type: 'mandatory',
				number: runningNumber++,
				green: project.green,
				amber: project.amber,
				red: project.red,
				total: project.total,
			}));
			const elective = (level.electiveProjects || []).map(project => ({
				value: project._id,
				label: project.title,
				type: 'elective',
				number: runningNumber++,
				green: project.green,
				amber: project.amber,
				red: project.red,
				total: project.total,
			}));

			options.push(...mandatory, ...elective);
		});

		const uniqueById = new Map();
		options.forEach(project => {
			if (!uniqueById.has(project.value)) {
				uniqueById.set(project.value, project);
			}
		});

		return Array.from(uniqueById.values());
	}, [selectedPathway]);

	useEffect(() => {
		if (!visible || !presenterId || !authUser?.token || userPathways.length === 0) return;

		let cancelled = false;

		const hydrateFromExistingAttemptForDate = async () => {
			const targetDateKey = toDateKey(
				scheduledDate ||
				toInputDate(item?.meetingDate) ||
				new Date().toISOString().slice(0, 10)
			);

			if (!targetDateKey) return;

			try {
				for (const userPathway of userPathways) {
					const projectsResponse = await fetch(
						`${url}/user/${presenterId}/userPathway/${userPathway._id}/projects`,
						{
							method: 'GET',
							headers: {
								'Content-Type': 'application/json',
								Authorization: `Bearer ${authUser?.token || ''}`,
							},
						}
					);

					if (!projectsResponse.ok) {
						continue;
					}

					const projects = await projectsResponse.json();
					const projectList = Array.isArray(projects) ? projects : [];

					for (const project of projectList) {
						const attempts = Array.isArray(project?.attempts) ? project.attempts : [];
						const existingAttempt = attempts.find((attempt) => toDateKey(attempt?.scheduledDate) === targetDateKey);

						if (!existingAttempt) {
							continue;
						}

						if (cancelled) return;

						setSelectedUserPathwayId(userPathway._id);
						setSelectedProjectId(project?.projectId || '');
						setSpeechTitle(existingAttempt?.speechTitle || '');
						setScheduledDate(toInputDate(existingAttempt?.scheduledDate) || targetDateKey);
						return;
					}
				}
			} catch (err) {
				// no-op: fallback to manual selection or subtitle-derived values
			}
		};

		hydrateFromExistingAttemptForDate();

		return () => {
			cancelled = true;
		};
	}, [visible, presenterId, authUser?.token, userPathways, scheduledDate, item?.meetingDate, url]);

	useEffect(() => {
		if (!visible || !item) return;

		const subtitle = String(item?.item_title || '').trim();
		const subtitleLines = subtitle
			.split(/\r?\n/)
			.map(line => line.trim())
			.filter(Boolean);
		const speechNameFromSubtitle = subtitleLines.length > 1 ? subtitleLines[0] : '';
		const currentTitle = speechNameFromSubtitle || item?.agendaEvent?.name || '';
		if (currentTitle) {
			setSpeechTitle(currentTitle);
		}

		const pathwayProjectLine = subtitleLines.length > 1
			? subtitleLines[subtitleLines.length - 1]
			: subtitle;
		const match = pathwayProjectLine.match(/^(.*?)\s*(?:-\s*)?#(\d+)\s*(?:-\s*)?(.*)$/);
		if (!match || !userPathways.length || !allPathways.length) return;

		const pathwayName = (match[1] || '').trim();
		const projectNumber = Number(match[2]);
		const projectName = (match[3] || '').trim();

		const matchedUserPathway = userPathways.find(up => {
			const linkedPathway = allPathways.find(p => p._id === up.pathwayId);
			return linkedPathway?.name === pathwayName;
		});

		if (!matchedUserPathway?._id) return;

		setSelectedUserPathwayId(matchedUserPathway._id);

		const linkedPathway = allPathways.find(p => p._id === matchedUserPathway.pathwayId);
		if (!linkedPathway) return;

		const projects = [];
		let runningNumber = 1;

		levels.forEach(levelKey => {
			const level = linkedPathway[levelKey] || {};
			(level.mandatoryProjects || []).forEach(project => {
				projects.push({ id: project._id, number: runningNumber++, name: project.title });
			});
			(level.electiveProjects || []).forEach(project => {
				projects.push({ id: project._id, number: runningNumber++, name: project.title });
			});
		});

		const matchedProject = projects.find(project =>
			project.number === projectNumber ||
			project.name === projectName
		);

		if (matchedProject?.id) {
			setSelectedProjectId(matchedProject.id);
		}
	}, [visible, item, userPathways, allPathways]);

	useEffect(() => {
		if (!visible || !presenterId || !selectedUserPathwayId || !selectedProjectId) return;

		const hydrateFromExistingAttempt = async () => {
			try {
				const projectsResponse = await fetch(
					`${url}/user/${presenterId}/userPathway/${selectedUserPathwayId}/projects`,
					{
						method: 'GET',
						headers: {
							'Content-Type': 'application/json',
							Authorization: `Bearer ${authUser?.token || ''}`,
						},
					}
				);

				if (!projectsResponse.ok) return;

				const projects = await projectsResponse.json();
				const projectList = Array.isArray(projects) ? projects : [];
				const hasSelectedUserProject = projectList.some(
					(project) => String(project?.projectId || '') === String(selectedProjectId)
				);

				if (!hasSelectedUserProject) return;

				const response = await fetch(
					`${url}/user/${presenterId}/userPathway/${selectedUserPathwayId}/project/${selectedProjectId}`,
					{
						method: 'GET',
						headers: {
							'Content-Type': 'application/json',
							Authorization: `Bearer ${authUser?.token || ''}`,
						},
					}
				);

				if (!response.ok) return;

				const userProject = await response.json();
				const attempts = Array.isArray(userProject?.attempts) ? userProject.attempts : [];
				const targetDateKey = toDateKey(
					scheduledDate ||
					toInputDate(item?.meetingDate) ||
					new Date().toISOString().slice(0, 10)
				);

				const existingAttempt = attempts.find(attempt => toDateKey(attempt?.scheduledDate) === targetDateKey);
				setExistingAttemptMeta(existingAttempt?._id ? {
					userPathwayId: selectedUserPathwayId,
					projectId: selectedProjectId,
					attemptId: existingAttempt._id,
				} : null);

				if (existingAttempt?.speechTitle) {
					setSpeechTitle(existingAttempt.speechTitle);
					setScheduledDate(toInputDate(existingAttempt?.scheduledDate) || targetDateKey);
				}
			} catch (err) {
				setExistingAttemptMeta(null);
				// no-op
			}
		};

		hydrateFromExistingAttempt();
	}, [visible, presenterId, selectedUserPathwayId, selectedProjectId, scheduledDate, item?.meetingDate, url, authUser?.token]);

	const handleCreateAttempt = async () => {
		if (!canEdit) {
			toast.error('You do not have permission to edit this pathway item.', { position: 'bottom-left' });
			return;
		}

		if (!presenterId || !selectedUserPathwayId || !selectedProjectId) {
			toast.error('Select presenter, pathway, and project first.', { position: 'bottom-left' });
			return;
		}

		if (!speechTitle.trim()) {
			toast.error('Attempt title is required.', { position: 'bottom-left' });
			return;
		}

		const scheduledDateToSave =
			scheduledDate ||
			toInputDate(item?.meetingDate) ||
			new Date().toISOString().slice(0, 10);

		setLoading(true);

		try {
			const linkedAttemptId = String(item?.pathwayAttempt?.attemptId || '').trim();
			const cleanupConflictingAttempts = async ({ targetDateKey, keepAttemptId }) => {
				for (const userPathway of userPathways) {
					const projectsResponse = await fetch(
						`${url}/user/${presenterId}/userPathway/${userPathway._id}/projects`,
						{
							method: 'GET',
							headers: {
								'Content-Type': 'application/json',
								Authorization: `Bearer ${authUser?.token || ''}`,
							},
						}
					);

					if (!projectsResponse.ok) {
						continue;
					}

					const projects = await projectsResponse.json();
					const projectList = Array.isArray(projects) ? projects : [];

					for (const project of projectList) {
						const attempts = Array.isArray(project?.attempts) ? project.attempts : [];

						for (const attempt of attempts) {
							if (toDateKey(attempt?.scheduledDate) !== targetDateKey) {
								continue;
							}

							const isTargetPathway = userPathway._id === selectedUserPathwayId;
							const isTargetProject = project?.projectId === selectedProjectId;
							const isKeptAttempt = Boolean(keepAttemptId) && attempt?._id === keepAttemptId;

							if (isTargetPathway && isTargetProject && isKeptAttempt) {
								continue;
							}

							// The attempt linked to this agenda item is moved (with its feedback) by the backend on save.
							if (linkedAttemptId && attempt?._id === linkedAttemptId) {
								continue;
							}

							await fetch(
								`${url}/user/${presenterId}/userPathway/${userPathway._id}/project/${project?.projectId}/attempt/${attempt?._id}`,
								{
									method: 'DELETE',
									headers: {
										'Content-Type': 'application/json',
										Authorization: `Bearer ${authUser?.token || ''}`,
									},
								}
							);
						}
					}
				}
			};

			const projectsResponse = await fetch(
				`${url}/user/${presenterId}/userPathway/${selectedUserPathwayId}/projects`,
				{
					method: 'GET',
					headers: {
						'Content-Type': 'application/json',
						Authorization: `Bearer ${authUser?.token || ''}`,
					},
				}
			);

			if (!projectsResponse.ok) {
				const errorData = await projectsResponse.json();
				throw new Error(errorData?.error || 'Unable to load user projects.');
			}

			const projects = await projectsResponse.json();
			const projectList = Array.isArray(projects) ? projects : [];
			let userProject = projectList.find(
				(project) => String(project?.projectId || '') === String(selectedProjectId)
			);

			if (!userProject) {
				const createProjectResponse = await fetch(
					`${url}/user/${presenterId}/userPathway/${selectedUserPathwayId}/project`,
					{
						method: 'POST',
						headers: {
							'Content-Type': 'application/json',
							Authorization: `Bearer ${authUser?.token || ''}`,
						},
						body: JSON.stringify({ projectId: selectedProjectId }),
					}
				);

				if (!createProjectResponse.ok) {
					const errorData = await createProjectResponse.json();
					throw new Error(errorData?.error || 'Unable to create user project.');
				}

				userProject = await createProjectResponse.json();
			}

			const existingAttempts = Array.isArray(userProject?.attempts) ? userProject.attempts : [];
			const targetDateKey = toDateKey(scheduledDateToSave);
			const existingAttemptForDate = existingAttempts.find(attempt => toDateKey(attempt?.scheduledDate) === targetDateKey);

			await cleanupConflictingAttempts({
				targetDateKey,
				keepAttemptId: existingAttemptForDate?._id,
			});

			let attemptResponse;

			if (existingAttemptForDate?._id) {
				attemptResponse = await fetch(
					`${url}/user/${presenterId}/userPathway/${selectedUserPathwayId}/project/${selectedProjectId}/attempt/${existingAttemptForDate._id}`,
					{
						method: 'PATCH',
						headers: {
							'Content-Type': 'application/json',
							Authorization: `Bearer ${authUser?.token || ''}`,
						},
						body: JSON.stringify({
							speechTitle: speechTitle.trim(),
							scheduledDate: scheduledDateToSave,
							// do NOT send feedbacks here — keeps any feedbacks already saved on this attempt
						}),
					}
				);
			} else {
				attemptResponse = await fetch(
					`${url}/user/${presenterId}/userPathway/${selectedUserPathwayId}/project/${selectedProjectId}/attempt`,
					{
						method: 'POST',
						headers: {
							'Content-Type': 'application/json',
							Authorization: `Bearer ${authUser?.token || ''}`,
						},
						body: JSON.stringify({
							speechTitle: speechTitle.trim(),
							scheduledDate: scheduledDateToSave,
						}),
					}
				);
			}

			const attemptResult = await attemptResponse.json();

			if (!attemptResponse.ok) {
				throw new Error(attemptResult?.error || 'Unable to create attempt.');
			}

			const attempts = Array.isArray(attemptResult?.attempts) ? attemptResult.attempts : [];
			const createdAttempt = existingAttemptForDate?._id
				? attempts.find(attempt => attempt?._id === existingAttemptForDate._id) || null
				: attempts[attempts.length - 1] || null;
			const selectedProject = projectOptions.find(option => option.value === selectedProjectId);

			if (createdAttempt?._id && onAttemptCreated) {
				await onAttemptCreated({
					userId: presenterId,
					userPathwayId: selectedUserPathwayId,
					projectId: selectedProjectId,
					attemptId: createdAttempt._id,
					removable: !existingAttemptForDate?._id,
					attemptTitle: speechTitle.trim(),
					pathwayName: selectedPathway?.name || '',
					projectName: selectedProject?.label || '',
					projectNumber: selectedProject?.number || '',
					green: selectedProject?.green,
					amber: selectedProject?.amber,
					red: selectedProject?.red,
					total: selectedProject?.total,
				});
			}

		} catch (err) {
			toast.error(err.message || 'Failed to create attempt.', { position: 'bottom-left' });
		} finally {
			setLoading(false);
		}
	};

	const canDeleteExistingAttempt = Boolean(existingAttemptMeta?.attemptId);
	const noAssignedPathways = Boolean(pathwaysLoaded && userPathways.length === 0);
	const pathwayOptions = userPathways.map(path => ({
		value: path._id,
		label: allPathways.find(ap => ap._id === path.pathwayId)?.name || path.pathway?.name || 'Unavailable pathway',
	}));
	const missingAssignedCatalogEntry = Boolean(
		catalogLoaded && selectedUserPathway && !selectedPathway
	);

	const handleSaveTitleOnly = async () => {
		if (!canEdit) {
			toast.error('You do not have permission to edit this pathway item.', { position: 'bottom-left' });
			return;
		}

		const trimmedTitle = speechTitle.trim();
		if (!trimmedTitle) {
			toast.error('Title is required.', { position: 'bottom-left' });
			return;
		}

		if (onAttemptCreated) {
			await onAttemptCreated({
				attemptTitle: trimmedTitle,
				removable: false,
			});
		}

	};

	const handleDeleteAttempt = async () => {
		if (!canEdit) {
			toast.error('You do not have permission to edit this pathway item.', { position: 'bottom-left' });
			return;
		}

		if (!presenterId || !existingAttemptMeta?.userPathwayId || !existingAttemptMeta?.projectId || !existingAttemptMeta?.attemptId) {
			toast.error('No existing attempt found for this date.', { position: 'bottom-left' });
			return;
		}

		const meetingDateKey = toDateKey(
			scheduledDate ||
			toInputDate(item?.meetingDate) ||
			new Date().toISOString().slice(0, 10)
		);
		const todayKey = getLocalTodayKey();

		if (!meetingDateKey || meetingDateKey < todayKey) {
			toast.error('Attempts cannot be deleted for past meetings.', { position: 'bottom-left' });
			return;
		}

		setLoading(true);
		try {
			const response = await fetch(
				`${url}/user/${presenterId}/userPathway/${existingAttemptMeta.userPathwayId}/project/${existingAttemptMeta.projectId}/attempt/${existingAttemptMeta.attemptId}`,
				{
					method: 'DELETE',
					headers: {
						'Content-Type': 'application/json',
						Authorization: `Bearer ${authUser?.token || ''}`,
					},
				}
			);

			if (!response.ok) {
				const errorData = await response.json().catch(() => null);
				throw new Error(errorData?.error || 'Unable to delete existing attempt.');
			}

			setSelectedUserPathwayId('');
			setSelectedProjectId('');
			setSpeechTitle('');
			setExistingAttemptMeta(null);
			setScheduledDate(toInputDate(item?.meetingDate) || '');
			if (onAttemptDeleted) {
				await onAttemptDeleted();
			}
		} catch (err) {
			toast.error(err.message || 'Failed to delete existing attempt.', { position: 'bottom-left' });
		} finally {
			setLoading(false);
		}
	};

	const isDark = document.body.getAttribute('data-theme') === 'dark';
	const selectStyles = {
		control: (provided, state) => ({
			...provided,
			backgroundColor: isDark ? '#23272b' : '#fff',
			color: isDark ? '#fff' : '#222',
			borderColor: state.isFocused ? (isDark ? '#FFD700' : '#004165') : (isDark ? '#444' : '#ccc'),
			boxShadow: state.isFocused ? `0 0 0 2px ${isDark ? '#FFD700' : '#004165'}` : 'none',
			minHeight: '42px',
		}),
		menu: (provided) => ({
			...provided,
			backgroundColor: isDark ? '#23272b' : '#fff',
			zIndex: 9999,
		}),
		singleValue: (provided) => ({
			...provided,
			color: isDark ? '#fff' : '#222',
		}),
		option: (provided, state) => ({
			...provided,
			backgroundColor: state.isSelected
				? (isDark ? '#FFD700' : '#004165')
				: state.isFocused
					? (isDark ? 'rgba(255,215,0,0.08)' : 'rgba(0,65,101,0.08)')
					: (isDark ? '#23272b' : '#fff'),
			color: state.isSelected
				? (isDark ? '#23272b' : '#fff')
				: (isDark ? '#fff' : '#222'),
			fontWeight: state.data.type === 'mandatory' ? 700 : 400,
			fontStyle: state.data.type === 'elective' ? 'italic' : 'normal',
		}),
		input: (provided) => ({
			...provided,
			color: isDark ? '#fff' : '#222',
		}),
		placeholder: (provided) => ({
			...provided,
			color: '#888',
		}),
	};

	if (!presenterId || !visible || !canEdit) return null;

	return (
		<Row className="justify-content-center mt-2 mb-3">
			<Col xs={12} lg={10}>
				<Alert variant={pathwaysError ? 'danger' : 'secondary'} className="mb-2">
					<strong>Pathway Project Attempt</strong>
					<div className="small">{pathwaysError
						? `Unable to load assigned pathways: ${pathwaysError}`
						: !pathwaysLoaded ? 'Loading assigned pathways...'
							: noAssignedPathways ? 'No assigned pathway was found for this presenter. You can save title only.'
								: 'Choose assigned pathway, project, and create an attempt for this meeting.'}</div>
					{pathwaysError && <Button variant="outline-danger" size="sm" className="mt-2" onClick={() => setPathwaysRetry(value => value + 1)}>Retry</Button>}
				</Alert>

				{!noAssignedPathways && (catalogError
					? <Alert variant="danger">
						<div>Unable to load the pathway catalogue: {catalogError}</div>
						<Button variant="outline-danger" size="sm" className="mt-2" onClick={() => setCatalogRetry(value => value + 1)}>Retry catalogue</Button>
					</Alert>
					: !catalogLoaded && <div role="status" className="mb-2">Loading pathway catalogue...</div>)}
				{missingAssignedCatalogEntry && <Alert variant="warning">
					This assigned pathway is not present in the configured TM Paths catalogue. Check that the selected catalogue contains this pathway.
				</Alert>}

				{pathwaysLoaded && !noAssignedPathways && (
					<Row className="mb-2">
						<Col xs={12} md={6}>
							<Form.Label className="small mb-1">Assigned Pathway</Form.Label>
							<Select
								options={pathwayOptions}
								value={pathwayOptions.find(option => option.value === selectedUserPathwayId) || null}
								onChange={(option) => {
									setSelectedUserPathwayId(option?.value || '');
									setSelectedProjectId('');
								}}
								placeholder="Select assigned pathway"
								styles={selectStyles}
								isDisabled={isPlaying}
							/>
						</Col>
						<Col xs={12} md={6}>
							<Form.Label className="small mb-1">Project</Form.Label>
							<Select
								options={projectOptions}
								value={projectOptions.find(option => option.value === selectedProjectId) || null}
								onChange={(option) => setSelectedProjectId(option?.value || '')}
								placeholder="Select project"
								styles={selectStyles}
								isDisabled={!selectedPathway || !catalogLoaded || isPlaying}
							/>
						</Col>
					</Row>
				)}

				<Row className="mb-2">
					<Col xs={12} md={9}>
						<Form.Label className="small mb-1">Attempt Title</Form.Label>
						<Form.Control
							value={speechTitle}
							onChange={(e) => setSpeechTitle(e.target.value)}
							placeholder="Enter attempt title"
							disabled={isPlaying}
						/>
					</Col>
					<Col xs={12} md={3} className="d-flex align-items-end">
						<div className="w-100 d-flex flex-column gap-2">
							<Button
								variant="success"
								className="w-100"
								onClick={noAssignedPathways ? handleSaveTitleOnly : handleCreateAttempt}
								disabled={!pathwaysLoaded || Boolean(pathwaysError) || (noAssignedPathways
									? (loading || isPlaying || !speechTitle.trim())
									: (loading || isPlaying || !catalogLoaded || !selectedPathway || !selectedUserPathwayId || !selectedProjectId)
								)}
							>
								{noAssignedPathways ? 'Save Title' : (loading ? 'Creating...' : 'Save Attempt')}
							</Button>
							{!noAssignedPathways && (
								<Button
									variant="outline-danger"
									className="w-100"
									onClick={handleDeleteAttempt}
									disabled={loading || isPlaying || !canDeleteExistingAttempt}
								>
									Delete Existing Attempt
								</Button>
							)}
						</div>
					</Col>
				</Row>
			</Col>
		</Row>
	);
}
